import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Film,
  Replace,
  Trash2,
  Upload,
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import '../../styles/module-shared.css';
import './education.css';
import {
  VIDEO_ACCEPT,
  PDF_ACCEPT,
  VIDEO_MAX_BYTES,
  PDF_MAX_BYTES,
  formatBytes,
  normalizeLessonContent,
  normalizeRichText,
  validatePdfFile,
  validateVideoFile,
} from './courseUtils';
import QuizBuilder from './QuizBuilder';

const THEME = getThemeColor('primary');

const ARTICLE_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const ARTICLE_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'align',
  'link',
];

const typeLabel = (type) => {
  if (type === 'PDF') return 'PDF';
  if (type === 'Video') return 'Video';
  if (type === 'Article') return 'Article';
  if (type === 'Quiz') return 'Quiz';
  return type || 'Lesson';
};

const LessonContent = () => {
  const { courseId, moduleId, lessonId } = useParams();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [content, setContent] = useState(null);
  const [articleHtml, setArticleHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getEducationLessonContent(lessonId);
      if (!res.success) {
        setError(res.error || res.message || 'Failed to load lesson content');
        setContent(null);
        return;
      }
      const normalized = normalizeLessonContent(res.data ?? res.result);
      if (!normalized) {
        setError('Lesson not found');
        return;
      }
      if (
        (normalized.courseId && Number(normalized.courseId) !== Number(courseId)) ||
        (normalized.moduleId && Number(normalized.moduleId) !== Number(moduleId))
      ) {
        setError('Lesson not found for this course/module');
        return;
      }
      setContent(normalized);
      setArticleHtml(normalized.articleContent || '');
      setSelectedFile(null);
      setFileError('');
    } catch (err) {
      setError(err.message || 'Failed to load lesson content');
    } finally {
      setLoading(false);
    }
  }, [courseId, moduleId, lessonId]);

  useEffect(() => {
    load();
  }, [load]);

  const backPath = useMemo(
    () => `/education/courses/${courseId}/modules/${moduleId}/lessons`,
    [courseId, moduleId]
  );

  const onSelectFile = (e, kind) => {
    const file = e.target.files?.[0] || null;
    e.target.value = '';
    if (!file) return;
    const err = kind === 'video' ? validateVideoFile(file) : validatePdfFile(file);
    if (err) {
      setFileError(err);
      setSelectedFile(null);
      return;
    }
    setFileError('');
    setSelectedFile(file);
  };

  const saveArticle = async () => {
    setSaving(true);
    try {
      const res = await apiService.saveEducationLessonArticle({
        courseLessonId: Number(lessonId),
        articleContent: normalizeRichText(articleHtml) || '',
      });
      if (!res.success) {
        showError(res.error || res.message || 'Failed to save article');
        return;
      }
      showSuccess(res.message || 'Article saved');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const uploadMedia = async (kind) => {
    if (!selectedFile) {
      setFileError(kind === 'video' ? 'Choose a video file first' : 'Choose a PDF file first');
      return;
    }
    setUploading(true);
    try {
      const res =
        kind === 'video'
          ? await apiService.uploadEducationLessonVideo(lessonId, selectedFile)
          : await apiService.uploadEducationLessonPdf(lessonId, selectedFile);
      if (!res.success) {
        showError(res.error || res.message || 'Upload failed');
        return;
      }
      showSuccess(res.message || 'Upload successful');
      setSelectedFile(null);
      await load();
    } finally {
      setUploading(false);
    }
  };

  const removeContent = async () => {
    if (!window.confirm('Remove this lesson content? The lesson itself will not be deleted.')) {
      return;
    }
    setSaving(true);
    try {
      const res = await apiService.deleteEducationLessonContent(lessonId);
      if (!res.success) {
        showError(res.error || res.message || 'Failed to remove content');
        return;
      }
      showSuccess(res.message || 'Content removed');
      setArticleHtml('');
      setSelectedFile(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner edu-lesson-page-inner">
          <AppLoader />
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner edu-lesson-page-inner">
          <Link to={backPath} className="edu-back">
            <ArrowLeft className="w-4 h-4" /> Back to lessons
          </Link>
          <ModuleAlert type="error" message={error || 'Lesson not found'} />
        </div>
      </div>
    );
  }

  const type = content.lessonType;
  const courseTitle = content.courseTitle || 'Course';
  const moduleTitle = content.moduleTitle || 'Module';
  const lessonTitle = content.title || 'Lesson';

  if (type === 'Quiz') {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner edu-lesson-page-inner">
          <QuizBuilder
            lessonId={lessonId}
            courseId={courseId}
            moduleId={moduleId}
            backPath={backPath}
            lessonMeta={{
              courseTitle: content.courseTitle,
              moduleTitle: content.moduleTitle,
              title: content.title,
              isActive: content.isActive,
              status: content.status,
            }}
            showSuccess={showSuccess}
            showError={showError}
          />
        </div>
      </div>
    );
  }

  const contentTitle =
    type === 'Article' ? 'Article Content' : type === 'Video' ? 'Lesson Video' : 'Lesson PDF';
  const contentDesc =
    type === 'Article'
      ? 'Write and format the article students will read in this lesson.'
      : type === 'Video'
        ? 'Upload or replace the video for this lesson.'
        : 'Upload or replace the PDF document for this lesson.';

  return (
    <div className="page edu-page" style={{ '--edu-primary': THEME }}>
      <div className="page-inner edu-lesson-page-inner">
        <div className="edu-lesson-shell">
          <nav className="edu-breadcrumb edu-quiz-breadcrumb" aria-label="Breadcrumb">
            <Link to="/education/courses">Education</Link>
            <span>/</span>
            <Link to="/education/courses">Courses</Link>
            <span>/</span>
            <Link to={`/education/courses/${courseId}`}>{courseTitle}</Link>
            <span>/</span>
            <Link to={backPath}>{moduleTitle}</Link>
            <span>/</span>
            <span>{typeLabel(type)}</span>
          </nav>

          <div className="edu-quiz-page-head">
            <div className="edu-quiz-page-head__main">
              <Link to={backPath} className="edu-back edu-quiz-back">
                <ArrowLeft className="w-4 h-4" /> Back to lessons
              </Link>
              <div className="edu-quiz-page-head__titles">
                <h1 className="edu-quiz-title">Lesson Content</h1>
                <p className="edu-quiz-subtitle">
                  Manage {typeLabel(type).toLowerCase()} content for this lesson.
                </p>
              </div>
            </div>
            <span className={`edu-status ${content.isActive ? 'is-active' : 'is-inactive'}`}>
              {content.status}
            </span>
          </div>

          <section className="module-card edu-card edu-quiz-info-card">
            <dl className="edu-quiz-info-grid">
              <div>
                <dt>Course</dt>
                <dd>{courseTitle}</dd>
              </div>
              <div>
                <dt>Module</dt>
                <dd>{moduleTitle}</dd>
              </div>
              <div>
                <dt>Lesson</dt>
                <dd>{lessonTitle}</dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{typeLabel(type)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className={`edu-status ${content.isActive ? 'is-active' : 'is-inactive'}`}>
                    {content.status}
                  </span>
                </dd>
              </div>
            </dl>
          </section>

          {type === 'Article' ? (
            <section className="module-card edu-card edu-lesson-workspace">
              <div className="edu-quiz-card-head edu-quiz-card-head--row">
                <div>
                  <h2 className="edu-quiz-card-title">{contentTitle}</h2>
                  <p className="edu-quiz-card-desc">{contentDesc}</p>
                </div>
              </div>

              <div className="edu-quill">
                <ReactQuill
                  theme="snow"
                  value={articleHtml}
                  onChange={setArticleHtml}
                  modules={ARTICLE_MODULES}
                  formats={ARTICLE_FORMATS}
                  readOnly={saving}
                />
              </div>

              <div className="edu-actions-row">
                <button
                  type="button"
                  className="edu-btn edu-btn--primary"
                  disabled={saving}
                  onClick={saveArticle}
                >
                  {saving ? 'Saving…' : 'Save Content'}
                </button>
                {content.hasContent ? (
                  <button
                    type="button"
                    className="edu-btn edu-btn--ghost"
                    disabled={saving}
                    onClick={removeContent}
                  >
                    <Trash2 className="w-4 h-4" /> Clear Article
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}

          {type === 'Video' ? (
            <section className="module-card edu-card edu-lesson-workspace">
              <div className="edu-quiz-card-head edu-quiz-card-head--row">
                <div>
                  <h2 className="edu-quiz-card-title">{contentTitle}</h2>
                  <p className="edu-quiz-card-desc">{contentDesc}</p>
                </div>
              </div>

              {content.contentFileUrl ? (
                <div className="edu-media-panel">
                  <div className="edu-media-panel__meta">
                    <span className="edu-media-panel__icon" aria-hidden="true">
                      <Film className="w-4 h-4" />
                    </span>
                    <div className="edu-media-panel__text">
                      <strong>{content.contentOriginalFileName || 'video'}</strong>
                      <span>Current video file</span>
                    </div>
                  </div>
                  <video
                    className="edu-video-player"
                    controls
                    src={content.contentFileUrl}
                    preload="metadata"
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
              ) : (
                <div className="edu-lesson-empty-state">
                  <div className="edu-lesson-empty-state__icon" aria-hidden="true">
                    <Film className="w-6 h-6" />
                  </div>
                  <h3>No video uploaded yet</h3>
                  <p>Choose a video file below to add lesson media.</p>
                </div>
              )}

              <div className="edu-upload-box">
                <div className="edu-upload-box__head">
                  <h3>{content.hasContent ? 'Replace Video' : 'Upload Video'}</h3>
                  <p>
                    MP4, WebM or MOV · max {formatBytes(VIDEO_MAX_BYTES)}
                  </p>
                </div>
                <div className="edu-upload-box__row">
                  <label className="edu-btn edu-btn--ghost edu-btn--sm" htmlFor="les-video">
                    <Upload className="w-4 h-4" /> Choose file
                  </label>
                  <input
                    id="les-video"
                    className="edu-file-input"
                    type="file"
                    accept={VIDEO_ACCEPT}
                    disabled={uploading || saving}
                    onChange={(e) => onSelectFile(e, 'video')}
                  />
                  {selectedFile ? (
                    <span className="edu-upload-box__selected">
                      {selectedFile.name} ({formatBytes(selectedFile.size)})
                    </span>
                  ) : (
                    <span className="edu-upload-box__selected is-muted">No file selected</span>
                  )}
                </div>
                {fileError ? <span className="edu-form-error">{fileError}</span> : null}
              </div>

              <div className="edu-actions-row">
                <button
                  type="button"
                  className="edu-btn edu-btn--primary"
                  disabled={uploading || saving || !selectedFile}
                  onClick={() => uploadMedia('video')}
                >
                  {uploading ? (
                    'Uploading…'
                  ) : content.hasContent ? (
                    <>
                      <Replace className="w-4 h-4" /> Replace Video
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" /> Upload Video
                    </>
                  )}
                </button>
                {content.hasContent ? (
                  <button
                    type="button"
                    className="edu-btn edu-btn--ghost"
                    disabled={uploading || saving}
                    onClick={removeContent}
                  >
                    <Trash2 className="w-4 h-4" /> Remove Video
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}

          {type === 'PDF' ? (
            <section className="module-card edu-card edu-lesson-workspace">
              <div className="edu-quiz-card-head edu-quiz-card-head--row">
                <div>
                  <h2 className="edu-quiz-card-title">{contentTitle}</h2>
                  <p className="edu-quiz-card-desc">{contentDesc}</p>
                </div>
              </div>

              {content.contentFileUrl ? (
                <div className="edu-media-panel edu-media-panel--pdf">
                  <div className="edu-media-panel__meta">
                    <span className="edu-media-panel__icon" aria-hidden="true">
                      <FileText className="w-4 h-4" />
                    </span>
                    <div className="edu-media-panel__text">
                      <strong>{content.contentOriginalFileName || 'document.pdf'}</strong>
                      <span>Current PDF file</span>
                    </div>
                    <a
                      className="edu-btn edu-btn--ghost edu-btn--sm"
                      href={content.contentFileUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FileText className="w-4 h-4" /> View PDF
                    </a>
                  </div>
                </div>
              ) : (
                <div className="edu-lesson-empty-state">
                  <div className="edu-lesson-empty-state__icon" aria-hidden="true">
                    <FileText className="w-6 h-6" />
                  </div>
                  <h3>No PDF uploaded yet</h3>
                  <p>Choose a PDF file below to add lesson material.</p>
                </div>
              )}

              <div className="edu-upload-box">
                <div className="edu-upload-box__head">
                  <h3>{content.hasContent ? 'Replace PDF' : 'Upload PDF'}</h3>
                  <p>PDF only · max {formatBytes(PDF_MAX_BYTES)}</p>
                </div>
                <div className="edu-upload-box__row">
                  <label className="edu-btn edu-btn--ghost edu-btn--sm" htmlFor="les-pdf">
                    <Upload className="w-4 h-4" /> Choose file
                  </label>
                  <input
                    id="les-pdf"
                    className="edu-file-input"
                    type="file"
                    accept={PDF_ACCEPT}
                    disabled={uploading || saving}
                    onChange={(e) => onSelectFile(e, 'pdf')}
                  />
                  {selectedFile ? (
                    <span className="edu-upload-box__selected">
                      {selectedFile.name} ({formatBytes(selectedFile.size)})
                    </span>
                  ) : (
                    <span className="edu-upload-box__selected is-muted">No file selected</span>
                  )}
                </div>
                {fileError ? <span className="edu-form-error">{fileError}</span> : null}
              </div>

              <div className="edu-actions-row">
                <button
                  type="button"
                  className="edu-btn edu-btn--primary"
                  disabled={uploading || saving || !selectedFile}
                  onClick={() => uploadMedia('pdf')}
                >
                  {uploading ? (
                    'Uploading…'
                  ) : content.hasContent ? (
                    <>
                      <Replace className="w-4 h-4" /> Replace PDF
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" /> Upload PDF
                    </>
                  )}
                </button>
                {content.hasContent ? (
                  <button
                    type="button"
                    className="edu-btn edu-btn--ghost"
                    disabled={uploading || saving}
                    onClick={removeContent}
                  >
                    <Trash2 className="w-4 h-4" /> Remove PDF
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LessonContent;
